// Auth context — httpOnly cookie session (no localStorage tokens)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All axios requests send cookies automatically
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoaded, setPurchasesLoaded] = useState(false);
  const [shopifyBilling, setShopifyBilling] = useState({ active: false, plan: null, tier: 'free' });

  // On mount, check if session cookie exists by calling /auth/me
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch {
        // No valid session — user is not logged in
        setUser(null);
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const fetchPurchases = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/purchases/mine`);
      setPurchases(res.data.purchases || []);
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    } finally {
      setPurchasesLoaded(true);
    }
  }, [user]);

  const fetchShopifyBilling = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopify/billing-context`);
      const billingData = {
        active: res.data.shopify_billing_active,
        plan: res.data.shopify_plan,
        tier: res.data.shopify_plan_tier || 'free',
        shopDomain: res.data.shop_domain,
        shopName: res.data.shop_name,
        pricingUrl: res.data.pricing_url || null,
      };
      setShopifyBilling(billingData);

      // If the user has an active Shopify connection but is on Free tier,
      // sync with Shopify to detect if they just approved a plan
      if (billingData.active && billingData.tier === 'free') {
        try {
          const syncRes = await axios.post(`${API}/shopify/sync-subscription`);
          if (syncRes.data.active && syncRes.data.plan === 'pro') {
            // Subscription was activated on Shopify — update local state
            setShopifyBilling(prev => ({ ...prev, plan: 'pro', tier: 'pro' }));
            // Refresh user to get updated subscription_tier
            const meRes = await axios.get(`${API}/auth/me`);
            setUser(meRes.data);
          }
        } catch {
          // Sync failed silently — will retry on next page load
        }
      }
    } catch {
      setShopifyBilling({ active: false, plan: null, tier: 'free' });
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchPurchases();
      fetchShopifyBilling();
    }
  }, [user, fetchPurchases, fetchShopifyBilling]);

  const hasProductAccess = useCallback((slug) => {
    if (!user) return false;
    if (user.subscription_tier === 'pro') return true;
    if (purchases.some(p => p.product_slug === slug)) return true;
    return purchases.some(p =>
      p.purchase_type === 'bundle' &&
      p.bundle_product_slugs &&
      p.bundle_product_slugs.includes(slug)
    );
  }, [user, purchases]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { user: userData } = response.data;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, full_name: fullName });
      const { user: userData } = response.data;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Clear state even if server call fails
    }
    setUser(null);
    setPurchases([]);
    setPurchasesLoaded(false);
  };

  const updateUser = (updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
    isPro: user?.subscription_tier === 'pro',
    isAdmin: user?.is_admin,
    isEmailVerified: user?.email_verified !== false,
    purchases,
    purchasesLoaded,
    fetchPurchases,
    hasProductAccess,
    // Shopify billing context (1.2.1 compliance)
    shopifyBilling,
    isShopifyBilled: shopifyBilling.active,
    refreshShopifyBilling: fetchShopifyBilling,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
